import React, { useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import axios from 'axios';
import { Formik, getIn, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import AlertBanner from '~/components/core/AlertBanner';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  concreteSelectedLobs,
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SysconfigOrganization } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import { NumericTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import type {
  AuthorityLevelInfo,
  CreateOverrideRuleParams,
  CreateOverrideRuleParamsLevelOverrideLimits,
  OverrideRuleWithOverridesInfo,
  UpdateOverrideRuleParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import DelegatedAuthorityConditionsQueryBuilderFormik from './QueryBuilder/DelegatedAuthorityConditionsQueryBuilderFormik';

type LimitField =
  | 'indemnity_payments_limit'
  | 'expenses_payments_limit'
  | 'indemnity_reserves_limit'
  | 'expenses_reserves_limit'
  | 'payments_limit'
  | 'reserves_limit';

interface LevelOverrideValues {
  enabled: boolean;
  indemnity_payments_limit: string;
  expenses_payments_limit: string;
  indemnity_reserves_limit: string;
  expenses_reserves_limit: string;
  payments_limit: string;
  reserves_limit: string;
}

const FIELD_IDS = {
  SUB_ORGANIZATION_IDS: 'sub_organization_ids',
  LOBS: 'lobs',
  ALL_LOBS: 'is_all_lobs',
  CONDITIONS_JSON: 'conditions_json',
} as const;

interface FormValues {
  display_name: string;
  conditions_json: RuleGroupType | null;
  overrides: Record<string, LevelOverrideValues>;
  sub_organization_ids: number[];
  // String LOB keys, plus the picker's numeric "All" sentinel when every LOB is selected — the same
  // mixed shape user interaction produces. Strip the sentinel with `concreteSelectedLobs` before
  // anything leaves the form.
  lobs: (string | number)[];
  is_all_lobs: boolean;
}

interface UpsertOverrideRuleDialogProps {
  organizationId: number;
  arePaymentLimitsSplit: boolean;
  levels: AuthorityLevelInfo[];
  existingRule: OverrideRuleWithOverridesInfo | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const splitLimitFields: LimitField[] = [
  'indemnity_payments_limit',
  'expenses_payments_limit',
  'indemnity_reserves_limit',
  'expenses_reserves_limit',
];
const unifiedLimitFields: LimitField[] = ['payments_limit', 'reserves_limit'];

const limitFieldLabels: Record<LimitField, string> = {
  indemnity_payments_limit: TEXTS.COLUMN_INDEMNITY_PAYMENTS,
  expenses_payments_limit: TEXTS.COLUMN_EXPENSE_PAYMENTS,
  indemnity_reserves_limit: TEXTS.COLUMN_INDEMNITY_RESERVES,
  expenses_reserves_limit: TEXTS.COLUMN_EXPENSE_RESERVES,
  payments_limit: TEXTS.COLUMN_PAYMENTS,
  reserves_limit: TEXTS.COLUMN_RESERVES,
};

const emptyLevelOverride = (): LevelOverrideValues => ({
  enabled: false,
  indemnity_payments_limit: '',
  expenses_payments_limit: '',
  indemnity_reserves_limit: '',
  expenses_reserves_limit: '',
  payments_limit: '',
  reserves_limit: '',
});

const buildInitialValues = (
  existingRule: OverrideRuleWithOverridesInfo | null,
  levels: AuthorityLevelInfo[],
  subOrganizationEnabled: boolean
): FormValues => {
  const overrides: Record<string, LevelOverrideValues> = {};
  for (const level of levels) {
    const existing = existingRule?.overrides?.[level.key];
    if (existing) {
      overrides[level.key] = {
        enabled: true,
        indemnity_payments_limit: existing.indemnity_payments_limit ?? '',
        expenses_payments_limit: existing.expenses_payments_limit ?? '',
        indemnity_reserves_limit: existing.indemnity_reserves_limit ?? '',
        expenses_reserves_limit: existing.expenses_reserves_limit ?? '',
        payments_limit: existing.payments_limit ?? '',
        reserves_limit: existing.reserves_limit ?? '',
      };
    } else {
      overrides[level.key] = emptyLevelOverride();
    }
  }

  // A new rule opens at "All". An existing rule's flags are read the way the engine matches them
  // (`is_all_lobs IS TRUE`): only an explicit `true` is org-wide, so a null is not shown — and then
  // re-saved — as a wider scope than the rule actually has.
  const isAllSubOrgs = existingRule ? existingRule.is_all_sub_orgs === true : true;
  const isAllLobs = existingRule ? existingRule.is_all_lobs === true : true;

  return {
    display_name: existingRule?.display_name ?? '',
    conditions_json: (existingRule?.conditions_json as RuleGroupType | null | undefined) ?? null,
    overrides,
    // Both scope fields are seeded in the pickers' own representation (the "All" sentinel), never in
    // the server shape (`lobs: []` alongside `is_all_lobs: true`). Seeding the server shape makes a
    // picker's mount conversion rewrite the field, which the clear-conditions listener below reads as
    // a user edit — silently discarding the stored conditions tree on every open. A rule's tree is
    // most of its payload here, so that loss would be silent and total.
    sub_organization_ids: !subOrganizationEnabled
      ? []
      : isAllSubOrgs
        ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
        : (existingRule?.sub_organization_ids ?? []),
    lobs: isAllLobs ? [LobMultiSelectWithChipsFormik.ALL_OPTION.id] : (existingRule?.lobs ?? []),
    is_all_lobs: isAllLobs,
  };
};

/** The server-shaped scope the two pickers' Formik values describe. Spread into both the create and
 *  the update body, which carry the same four fields. */
const buildScopePayload = (
  values: FormValues,
  subOrganizationEnabled: boolean
): Pick<CreateOverrideRuleParams, 'is_all_sub_orgs' | 'sub_organization_ids' | 'is_all_lobs' | 'lobs'> => {
  // Without sub-organizations the engine stores every rule as all-sub-orgs whatever it is sent
  // (`_set_up_rule_sub_organizations`), so send exactly that.
  const isAllSubOrgs = !subOrganizationEnabled || isAllSubOrganizationsSelected(values.sub_organization_ids);
  return {
    is_all_sub_orgs: isAllSubOrgs,
    sub_organization_ids: isAllSubOrgs ? [] : values.sub_organization_ids,
    is_all_lobs: values.is_all_lobs,
    // `concreteSelectedLobs` is what keeps the numeric "All" sentinel from reaching the wire as a
    // LOB key named "0".
    lobs: values.is_all_lobs ? [] : concreteSelectedLobs(values.lobs ?? []),
  };
};

const buildOverridesPayload = (values: FormValues): Record<string, CreateOverrideRuleParamsLevelOverrideLimits> => {
  const result: Record<string, CreateOverrideRuleParamsLevelOverrideLimits> = {};
  for (const [levelKey, override] of Object.entries(values.overrides)) {
    if (!override.enabled) continue;
    const limits: CreateOverrideRuleParamsLevelOverrideLimits = {};
    for (const field of [...splitLimitFields, ...unifiedLimitFields]) {
      const raw = override[field];
      if (raw !== '' && raw !== null && raw !== undefined) {
        limits[field] = raw;
      }
    }
    if (Object.keys(limits).length === 0) continue;
    result[levelKey] = limits;
  }
  return result;
};

// An emptied scope is not "no narrowing": `is_all_lobs: false` with `lobs: []` would scope the rule
// to no LOB at all, which can never match a claim. The server refuses it, and an emptied
// sub-organization scope, with a 400; checking here shows the reason on the field before the save.
const createValidationSchema = (subOrganizationEnabled: boolean) =>
  Yup.object().shape({
    display_name: Yup.string().trim().required(TEXTS.FIELD_DISPLAY_NAME_REQUIRED),
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().min(1, TEXTS.VALIDATION_SUB_ORGANIZATION_REQUIRED)
      : Yup.array(),
    [FIELD_IDS.LOBS]: Yup.array().when(FIELD_IDS.ALL_LOBS, {
      is: false,
      then: (schema) => schema.min(1, TEXTS.VALIDATION_LOB_REQUIRED),
      otherwise: (schema) => schema,
    }),
    overrides: Yup.object().test('at-least-one', TEXTS.VALIDATION_OVERRIDES_REQUIRED, (value) =>
      Object.values(value ?? {}).some((override) => (override as LevelOverrideValues)?.enabled)
    ),
  });

interface LevelOverrideRowProps {
  level: AuthorityLevelInfo;
  arePaymentLimitsSplit: boolean;
}

const LevelOverrideRow: React.FC<LevelOverrideRowProps> = ({ level, arePaymentLimitsSplit }) => {
  const { values } = useFormikContext<FormValues>();
  const fieldRoot = `overrides.${level.key}`;
  const enabled = !!getIn(values, `${fieldRoot}.enabled`);
  const limitFields = arePaymentLimitsSplit ? splitLimitFields : unifiedLimitFields;

  return (
    <div className="flex flex-col gap-8 rounded-md bg-slate-200 p-12">
      <CheckboxFormik id={`${fieldRoot}.enabled`} label={`${TEXTS.OVERRIDE_LEVEL_TOGGLE}: ${level.name}`} />
      {enabled && (
        <div className="grid grid-cols-2 gap-12">
          {limitFields.map((field) => (
            <NumericTextFieldFormik
              key={field}
              label={limitFieldLabels[field]}
              id={`${fieldRoot}.${field}`}
              fullWidth
              allowNegative
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DialogInner: React.FC<{
  onClose: () => void;
  isNew: boolean;
  arePaymentLimitsSplit: boolean;
  levels: AuthorityLevelInfo[];
  organizationId: number;
  subOrganizationEnabled: boolean;
  subOrganizations: { id: number; name: string }[];
}> = ({ onClose, isNew, arePaymentLimitsSplit, levels, organizationId, subOrganizationEnabled, subOrganizations }) => {
  const { handleSubmit, isSubmitting, errors, submitCount, values, setFieldValue } = useFormikContext<FormValues>();
  const [areConditionsLoading, setAreConditionsLoading] = useState(false);
  const overridesError = typeof errors.overrides === 'string' && submitCount > 0 ? (errors.overrides as string) : null;

  // Read off the picker's own value rather than `is_all_lobs`: that flag is written by the picker's
  // change listener one render later, and the intervening render would fetch the catalog with the
  // sentinel still sitting in `lobs`.
  const isAllLobs = isAllLobsSelected(values.lobs ?? []);
  const catalogLobs = isAllLobs ? [] : concreteSelectedLobs(values.lobs ?? []);
  const isAllSubOrgs = subOrganizationEnabled ? isAllSubOrganizationsSelected(values.sub_organization_ids) : true;

  // Clear the conditions tree whenever a scope dimension changes: the catalog refetches on these
  // keys (LOB / sub-org narrow which providers contribute), so a tree authored under the old scope
  // must not silently carry over. Delegated authority has no trigger event to listen on.
  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: () => setFieldValue(FIELD_IDS.CONDITIONS_JSON, null),
    runOnFirstRender: false,
  });

  return (
    <CardDialog
      title={isNew ? TEXTS.CREATE_OVERRIDE_RULE : TEXTS.EDIT_OVERRIDE_RULE}
      isDialog
      open
      maxWidth="md"
      fullWidth
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel={TEXTS.SAVE}
          showPrimary
          secondaryLabel={TEXTS.CANCEL}
          disabled={isSubmitting || areConditionsLoading}
        />
      }
    >
      <div className="flex flex-col gap-20">
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          title={TEXTS.SCOPE_CHANGE_BANNER_TITLE}
          note={TEXTS.SCOPE_CHANGE_BANNER}
          withIcon
        />
        <TextFieldFormik label={TEXTS.FIELD_DISPLAY_NAME} id="display_name" className="w-full" />
        <div className="flex flex-col gap-8">
          <Heading variant={Heading.TYPES.H4}>{TEXTS.SECTION_SCOPE}</Heading>
          {subOrganizationEnabled && (
            <SubOrgMultiSelectWithChipsFormik
              id={FIELD_IDS.SUB_ORGANIZATION_IDS}
              label={TEXTS.FIELD_SUB_ORGANIZATIONS}
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          )}
          <LobMultiSelectWithChipsFormik
            lobsFieldId={FIELD_IDS.LOBS}
            allLobsFieldId={FIELD_IDS.ALL_LOBS}
            label={TEXTS.FIELD_LOBS}
            showOnly={isSubmitting}
            {...(subOrganizationEnabled
              ? {
                  subOrganizationIds: isAllSubOrgs
                    ? subOrganizations.map((subOrganization) => subOrganization.id)
                    : values.sub_organization_ids,
                  subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                  shouldResetOnSubOrgChange: true,
                  disabled: isSubmitting || isEmpty(values.sub_organization_ids),
                }
              : { disabled: isSubmitting })}
          />
        </div>
        <DelegatedAuthorityConditionsQueryBuilderFormik
          organizationId={organizationId}
          // Every picked LOB: the catalog is the cross-LOB intersection, which is also what
          // save-time validation checks the rule against.
          lobs={catalogLobs}
          isAllLobs={isAllLobs}
          isAllSubOrgs={isAllSubOrgs}
          subOrgIds={isAllSubOrgs ? [] : values.sub_organization_ids}
          onLoadingChange={setAreConditionsLoading}
        />
        <div className="flex flex-col gap-8">
          <Heading variant={Heading.TYPES.H4}>{TEXTS.SECTION_PER_LEVEL_OVERRIDES}</Heading>
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.PER_LEVEL_OVERRIDES_HELP}
          </Text>
          {levels.map((level) => (
            <LevelOverrideRow key={level.key} level={level} arePaymentLimitsSplit={arePaymentLimitsSplit} />
          ))}
          {overridesError && (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.ERROR}>
              {overridesError}
            </Text>
          )}
        </div>
      </div>
    </CardDialog>
  );
};

const UpsertOverrideRuleDialog: React.FC<UpsertOverrideRuleDialogProps> = ({
  organizationId,
  arePaymentLimitsSplit,
  levels,
  existingRule,
  onClose,
  onSaved,
}) => {
  // The *configured* organization, not the admin's own — this dialog only ever renders under
  // SystemConfiguration, so `useSysconfig()` is the correct context and `useOrganization()` would
  // gate the sub-org picker on whoever is signed in.
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const subOrganizationEnabled = !!organization.sub_organizations_enabled;
  const subOrganizations = organization.sub_organizations ?? [];
  const isNew = !existingRule;
  const baseUrl = `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/override_rules`;

  const handleSubmit = async (values: FormValues) => {
    const overrides = buildOverridesPayload(values);
    const scope = buildScopePayload(values, subOrganizationEnabled);
    // The QB initialises an untouched tree to `{combinator: 'and', rules: []}`;
    // normalise that to `null` so the BE treats it as "no conditions" instead
    // of compiling an empty CEL group.
    const conditions_json =
      values.conditions_json && values.conditions_json.rules.length > 0 ? values.conditions_json : null;
    try {
      if (isNew) {
        const payload: CreateOverrideRuleParams = {
          display_name: values.display_name.trim(),
          conditions_json,
          overrides,
          ...scope,
        };
        await axios.post(baseUrl, payload);
      } else {
        const payload: UpdateOverrideRuleParams = {
          display_name: values.display_name.trim(),
          conditions_json,
          overrides,
          ...scope,
        };
        await axios.patch(`${baseUrl}/${existingRule.rule_id}`, payload);
      }
    } catch (error) {
      await reportAxiosError(error);
      return;
    }
    onClose();
    try {
      await onSaved();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={buildInitialValues(existingRule, levels, subOrganizationEnabled)}
      onSubmit={handleSubmit}
      validationSchema={createValidationSchema(subOrganizationEnabled)}
    >
      <DialogInner
        onClose={onClose}
        isNew={isNew}
        arePaymentLimitsSplit={arePaymentLimitsSplit}
        levels={levels}
        organizationId={organizationId}
        subOrganizationEnabled={subOrganizationEnabled}
        subOrganizations={subOrganizations}
      />
    </Formik>
  );
};

export default UpsertOverrideRuleDialog;
